import React, { useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { IFormState, IField, IFieldElement, TComputed } from '../../types/calculator';

import { useCleanDisabledSelector } from './hooks/useCleanDisabledSelector';
import { useSelectSingleAvailableValue } from './hooks/useSelectSingleAvailableValue';

/** Вычисляет значение из переданной функции, если передана функция, иначе возвращает значение **/
function computeOrValue<TYPE>(value: TYPE | TComputed<TYPE> | undefined, state: IFormState) {
  return typeof value === 'function' ? (value as TComputed<TYPE>)(state) : value;
}

export function useBaseField(props: IField) {
  const { control, trigger } = useFormContext<IFormState>();
  const {
    fieldState: { invalid, isDirty, isTouched, error },
    field: { name, value, onChange, onBlur, ref },
  } = useController<IFormState, typeof props.name>({
    control,
    name: props.name,
    rules: props.validation,
  });

  useCleanDisabledSelector({ source: props?.source, value, onChange, defaultValue: props?.defaultValue });
  /**
   * `downPayment` + InputWithOptions: `source` — % чипов, в RHF — рубли; при смене `source` (регион и т.д.)
   * `useSelectSingleAvailableValue` вызывал onChange(процент) и портил значение для `getValues`/событий.
   */
  useSelectSingleAvailableValue({
    source: props?.source,
    value,
    onChange,
    isDirty,
    enabled: name !== 'downPayment',
  });

  /* oxlint-disable typescript/no-explicit-any */
  const handleChange = async (...payload: any | any[]) => {
    onChange(...payload);
    await trigger();
  };

  return {
    fieldState: { invalid, isDirty, isTouched, error: error?.message },
    field: { name, value, onChange: handleChange, onBlur, ref },
  };
}

export interface IWithComputedField {
  field: IFieldElement;
  children: (field: IField) => React.ReactElement | null;
}
export const WithComputedField: React.FC<IWithComputedField> = ({ field, children }) => {
  const { watch } = useFormContext<IFormState>();
  const formState = watch();

  const fieldSchemeComputed = useMemo<IField>(() => {
    return {
      ...field,
      type: field.type,
      hidden: computeOrValue(field.hidden, formState),
      source: computeOrValue(field.source, formState),
      placeholder: computeOrValue(field.placeholder, formState),
      rightAdornment: computeOrValue(field.rightAdornment, formState),
      disabled: computeOrValue(field.disabled, formState),
    } satisfies IField;
  }, [field, formState]);

  return children(fieldSchemeComputed);
};
